import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Eye, Share2, Link2, ChevronRight, Settings } from 'lucide-react';
import ShareBusinessCard from '../components/ShareBusinessCard';
import { usePageHeader } from '../context/PageHeaderContext';

const BusinessCardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showShareSheet, setShowShareSheet] = useState(false);
  const isAdmin = user?.role === 'admin';
  usePageHeader({
    title: 'Business Card',
    rightSlot: isAdmin ? (
      <button
        onClick={() => navigate('/admin/business-cards')}
        className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
        aria-label="Manage Business Cards"
        data-testid="header-manage-business-cards-btn"
        title="Manage Business Cards"
      >
        <Settings className="w-5 h-5" />
      </button>
    ) : null,
  }, [isAdmin]);

  const options = [
    {
      id: 'link',
      label: 'Link Physical Card',
      description: 'Connect your NFC business card',
      icon: Link2,
      color: 'from-blue-500 to-indigo-600',
      action: () => navigate('/business-card/link')
    },
    {
      id: 'view',
      label: 'View Business Card',
      description: 'Preview your digital business card',
      icon: Eye,
      color: 'from-emerald-500 to-teal-600',
      action: () => navigate('/business-card/view')
    },
    {
      id: 'share',
      label: 'Share Business Card',
      description: 'Share via link, text, or ORB',
      icon: Share2,
      color: 'from-violet-500 to-purple-600',
      action: () => setShowShareSheet(true)
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="business-card-page">
      <div className="space-y-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          return (
            <Card
              key={opt.id}
              className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg cursor-pointer hover:shadow-xl transition-all active:scale-[0.98]"
              onClick={opt.action}
              data-testid={`business-card-${opt.id}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${opt.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{opt.label}</p>
                  <p className="text-xs text-muted-foreground">{opt.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Share Bottom Sheet — pass the decoy id so the URL shipped
       *  to recipients never exposes the real internal user id. The
       *  fallback to user?.id covers users who pre-date the migration
       *  in case startup backfill hasn't run yet. */}
      <ShareBusinessCard
        open={showShareSheet}
        onClose={() => setShowShareSheet(false)}
        userId={user?.card_decoy_id || user?.id}
      />
    </div>
  );
};

export default BusinessCardPage;
